import { lazy, Suspense, useMemo } from "react";
import { Route, Routes } from "react-router";
import { SuspenseFallback } from "components/layout/suspense/SuspenseFallback";
import { SuspenseWithErrorBoundry } from "components/layout/suspense/SuspenseWithErrorBoundry";
import { ErrorBoundryFallback } from "components/layout/suspense/ErrorBoundryFallback";

import { RouteGuardDefault } from "./components/routes/RouteGuardDefault";
import { RouteGuardApplicationRights } from "./components/routes/RouteGuardApplicationRights";


import { ApplicationRightSystemNames } from './components/constants/ApplicationRightSystemNames';

import Layout from "./components/layout/Layout";
import NotFound from "./components/access/NotFound";

import { Index as HomeIndex } from "./pages/home/Index";
import Logout from "./pages/home/Logout";

import { ModuleService } from "services/ModuleService";

// Help
const HelpIndexLazy = lazy(() => import("./pages/help/Index").then((module) => ({ default: module.Index })));

// Pilotage
import PilotagePilotagesCurrent from "./pages/pilotages/Current";
const PilotageDetailsReadOnlyLazy = lazy(() => import("./pages/pilotages/Details"));



// Pilot
// brukes??
import PilotPilotageDetails from "./pages/pilot/pilotages/Details";
import PilotPilotagesIndex from "./pages/pilot/pilotages/Index";


// Incident
const IncidentIndexLazy = lazy(() => import("./pages/incident/Index"));
const IncidentEditLazy = lazy(() => import("./pages/incident/edit/Index"));
const IncidentCreateLazy = lazy(() => import("./pages/incident/create/Index"));
const IncidentShipSearchLazy = lazy(() => import("./pages/incident/ShipSearch"));
const IncidentSelectedShipLazy = lazy(() => import("./pages/incident/SelectedShip"));

export const AppRoutes = () => {
    const AppRoutesDefault = [
        {
            element: <Logout />,
            path: "/logout",
        },
    ];

    const AppRoutesApplicationRights = [
        {
            path: "/",
            element: <HomeIndex />,
            applicationRights: [],
            onlineOnly: false,
        },
        {
            path: "/help",
            lazy: <HelpIndexLazy />,
            applicationRights: [],
        },

        // Pilotage
        {
            path: "/pilotages/current",
            element: <PilotagePilotagesCurrent />,
            applicationRights: [ApplicationRightSystemNames.WEB_MODULE_CURRENT_PILOTAGES_READ],
        },
        {
            path: "/pilotages/current/details/:id/:view?",
            element: <PilotageDetailsReadOnlyLazy />,
            applicationRights: [ApplicationRightSystemNames.WEB_MODULE_CURRENT_PILOTAGES_READ],
        },

        // Pilot
        {
            path: "/pilot/pilotages/:view?",
            element: <PilotPilotagesIndex />,
            applicationRights: [ApplicationRightSystemNames.WEB_MODULE_MY_PILOT_ASSIGNMENTS_WRITE],
            onlineOnly: false,
            doGuestCheck: true,
        },
        {
            path: "/pilot/pilotages/details/:id/:view?",
            element: <PilotPilotageDetails />,
            applicationRights: [ApplicationRightSystemNames.WEB_MODULE_MY_PILOT_ASSIGNMENTS_WRITE],
            onlineOnly: false,
            doGuestCheck: true,
        },


        // Incident: online only??
        {
            lazy: <IncidentIndexLazy />,
            applicationRights: [ApplicationRightSystemNames.WEB_MODULE_INCIDENTS_CASEHANDLER_WRITE, ApplicationRightSystemNames.WEB_MODULE_INCIDENTS_WRITE],
            path: "/incident",
            minWidth: 660,
        },
        {
            lazy: <IncidentEditLazy />,
            applicationRights: [ApplicationRightSystemNames.WEB_MODULE_INCIDENTS_CASEHANDLER_WRITE, ApplicationRightSystemNames.WEB_MODULE_INCIDENTS_WRITE],
            path: "/incident/:id",
            minWidth: 660,
        },
        {
            lazy: <IncidentCreateLazy />,
            applicationRights: [ApplicationRightSystemNames.WEB_MODULE_INCIDENTS_CASEHANDLER_WRITE, ApplicationRightSystemNames.WEB_MODULE_INCIDENTS_WRITE],
            path: "/incident/create/:id",
            minWidth: 660,
        },
        {
            lazy: <IncidentShipSearchLazy />,
            applicationRights: [ApplicationRightSystemNames.WEB_MODULE_INCIDENTS_CASEHANDLER_WRITE, ApplicationRightSystemNames.WEB_MODULE_INCIDENTS_WRITE],
            path: "/incident/shipsearch",
            minWidth: 660,
        },
        {
            lazy: <IncidentSelectedShipLazy />,
            applicationRights: [ApplicationRightSystemNames.WEB_MODULE_INCIDENTS_CASEHANDLER_WRITE, ApplicationRightSystemNames.WEB_MODULE_INCIDENTS_WRITE],
            path: "/incident/shipsearch/:id",
            minWidth: 660,
        },
        
    ];

    const modulesWithRoutes = useMemo(
        () => ModuleService.getModules().filter(module => module.routes),
        []
    );
    
    return (
        <Routes>
            
            {/* RouteGuardDefault */}
            {AppRoutesDefault.map((route, index) => (
                <Route
                    key={index}
                    path={route.path}
                    element={
                        <Layout>
                            <RouteGuardDefault minWidth={route.minWidth ?? 0}>{route.element}</RouteGuardDefault>
                        </Layout>
                    }
                />
            ))}

            {/* RouteGuardApplicationRights */}
            {AppRoutesApplicationRights.map((route, index) => (
                <Route
                    key={index}
                    path={route.path}
                    element={
                        <Layout>
                            <RouteGuardApplicationRights
                                onlineOnly={route.onlineOnly}
                                requiredApplicationRights={route.applicationRights}
                                minWidth={route.minWidth ?? 0}>
                                {route.lazy ? (
                                    <LazyRouteElement fallback={route.fallback}>{route.lazy}</LazyRouteElement>
                                ) : (
                                    route.element
                                )}
                            </RouteGuardApplicationRights>
                        </Layout>
                    }
                />
            ))}

            {/* Dynamic module routes */}
            {modulesWithRoutes.map((module) => module.routes?.())}

            <Route
                path="*"
                element={
                    <Layout>
                        <NotFound />
                    </Layout>
                }
            />
        </Routes>
    );
};

function LazyRouteElement({ children, fallback }) {
    if (!children) return null;
    return (
        <SuspenseWithErrorBoundry
            suspenseFallback={fallback ? <Suspense fallback={fallback} /> : <SuspenseFallback />}
            errorBoundryFallback={<ErrorBoundryFallback mode="text" text="Kunne ikke laste inn siden..." />}
        >
            {children}
        </SuspenseWithErrorBoundry>
    );
}
